import formatEuros from '@UI/Helpers/formatEuros';
import GridCard from '@Guidelines/Component/GridCard';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import ModelCardActions from '@UI/Pages/Administration/AiEngines/Sections/ModelsSection/ModelCard/ModelCardActions';
import type AiModelView from '@UI/Store/AiConfiguration/Views/AiModelView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';
import { Tag } from '@verclam/ui-kit';

const MODEL_CARD_TRACK_COUNT = 3;

const headerStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.125rem;
`;

const nameStyle = css`
	${theme.typography.cardTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

const subTitleStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const bodyStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const fieldRowStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.125rem;
`;

const fieldLabelStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const fieldValueStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.ink};
`;

const badgeRowStyle = css`
	display: flex;
	flex-wrap: wrap;
	gap: 0.5rem;
`;

const testMessageStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.states.danger.ink};
`;

// `warning`, never `danger`: rejected settings mostly surface on a SUCCESSFUL test — a model can
// answer correctly while ignoring a setting it does not support.
const rejectedSettingsStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.states.warning.ink};
`;

type Props = {
	readonly model: AiModelView;
};

/**
 * One catalogued model: provider name, identifier, its authorized usages and the outcome of its last
 * connection test. `usages` carries bare enum values (never `{ value, label }`, unlike
 * `lastTestedUsage`): labels are resolved here from `aiUsagesStore`, already loaded by the section that
 * mounts this card.
 *
 * Laid out as a three-track subgrid cell: a header block (title plus provider subtitle, stacked with
 * their own tight spacing), body, actions. The header stays a single track so the subtitle sits close
 * to its title regardless of the row's gap — splitting it into its own track would let the row's gap
 * and the title's own height push the subtitle away from it. Everything that varies from one model to
 * another — identifier, the variable-length usage badges, the test outcome badge, the conditional
 * failure message — stays inside the single body track rather than becoming a track of its own.
 */
const ModelCard: React.FC<Props> = ({ model }) => {
	const providers = useSelector((state) => state.aiConfigurationStore.providers);
	const aiUsages = useSelector((state) => state.aiUsagesStore.aiUsages);

	const providerLabel = React.useMemo(() => {
		return providers?.findById(model.providerId)?.label ?? 'Fournisseur inconnu';
	}, [providers, model.providerId]);

	const usageBadges = React.useMemo(() => {
		return model.usages.map((usage) => ({
			usage,
			label: aiUsages?.find((entry) => entry.value === usage)?.label ?? usage,
		}));
	}, [aiUsages, model.usages]);

	const priceLines = React.useMemo(() => {
		const prices: ReadonlyArray<readonly [number | null, string]> = [
			[model.inputPricePerMillionTokens, 'M jetons en entrée'],
			[model.outputPricePerMillionTokens, 'M jetons en sortie'],
			[model.cachedInputPricePerMillionTokens, 'M jetons lus en cache'],
			[model.cacheWritePricePerMillionTokens, 'M jetons écrits en cache'],
			[model.pricePerAudioMinute, 'min d’audio'],
		];

		return prices.flatMap(([price, unit]) => (price !== null ? [`${formatEuros(price)} / ${unit}`] : []));
	}, [model]);

	const testBadge = React.useMemo(() => {
		if (model.lastTestSucceeded === null) {
			return <Tag severity="secondary" value="Connexion jamais testée" />;
		}

		if (model.lastTestSucceeded) {
			return (
				<Tag
					severity="success"
					value={
						model.lastTestedUsage !== null
							? `Connexion réussie (${model.lastTestedUsage.label})`
							: 'Connexion réussie'
					}
				/>
			);
		}

		return <Tag severity="danger" value="Connexion en échec" />;
	}, [model]);

	return (
		<GridCard trackCount={MODEL_CARD_TRACK_COUNT} rowGap="0.875rem">
			<div css={headerStyle}>
				<h2 css={nameStyle}>{model.label}</h2>
				<p css={subTitleStyle}>{providerLabel}</p>
			</div>
			<div css={bodyStyle}>
				<div css={fieldRowStyle}>
					<p css={fieldLabelStyle}>Identifiant</p>
					<p css={fieldValueStyle}>{model.identifier}</p>
				</div>
				{model.family !== null ? (
					<div css={fieldRowStyle}>
						<p css={fieldLabelStyle}>Famille</p>
						<p css={fieldValueStyle}>{model.family.label}</p>
					</div>
				) : null}
				<ConditionalLoader data={model.deliveryMode}>
					{(deliveryMode) => (
						<div css={fieldRowStyle}>
							<p css={fieldLabelStyle}>Mode d’envoi</p>
							<p css={fieldValueStyle}>{deliveryMode.label}</p>
						</div>
					)}
				</ConditionalLoader>
				<div css={badgeRowStyle}>
					{usageBadges.map((badge) => (
						<Tag key={badge.usage} severity="secondary" value={badge.label} />
					))}
				</div>
				<ConditionalLoader
					data={model.hasPrices}
					fallback={<Tag severity="warning" value="Tarif non renseigné" />}
				>
					<div css={fieldRowStyle}>
						<p css={fieldLabelStyle}>Tarif</p>
						{priceLines.map((line) => (
							<p key={line} css={fieldValueStyle}>
								{line}
							</p>
						))}
					</div>
				</ConditionalLoader>
				<div css={badgeRowStyle}>{testBadge}</div>
				{model.lastTestSucceeded === false && model.lastTestMessage !== null ? (
					<p css={testMessageStyle}>{model.lastTestMessage}</p>
				) : null}
				{model.lastTestRejectedSettings !== null && model.lastTestRejectedSettings.length > 0 ? (
					<p css={rejectedSettingsStyle}>
						Réglages non acceptés par le modèle : {model.lastTestRejectedSettings.join(', ')}
					</p>
				) : null}
			</div>
			<ModelCardActions model={model} />
		</GridCard>
	);
};

export default ModelCard;
